import axiosInstance from "../index";
import { CountryType } from '@src/utilities/types';

/**
 * Retrieves referential data of a specified type.
 *
 * @param type - The type of referential data to retrieve.
 * @return {Promise<any>} A promise that resolves to the retrieved referential data.
 */
type getReferencialDataProps = {
    type: string;
}
export const getReferencialData = async({ type }: getReferencialDataProps): Promise<any> => {
    try {
        const {data} = await axiosInstance.get(`/hub/referential/${type}`)
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Retrieves the list of countries from the API.
 *
 * @return {Promise<CountryType[]>} A promise that resolves to an array of CountryType objects.
 */
export const getCountries = async(): Promise<CountryType[]> => {
    try {
        const { data } = await axiosInstance.get<CountryType[]>('/hub/referential/countries');
        let formattedData: CountryType[] = data.filter((value, index, self) => 
            index === self.findIndex((obj) => (
                obj.phone_extension === value.phone_extension && value.phone_extension && value.numeric && value.alpha2 && value.numeric && value.alpha3
            ))
        )
        return Promise.resolve(formattedData);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Searches for addresses based on a search value.
 *
 * @param {searchAddressParams} searchValue - The search value for addresses.
 * @return {Promise<searchAddressResult[]>} - A promise that resolves to an array of search results.
 */
type searchAddressParams = {
    searchValue: string
}

type searchAddressResult = {
    city: string,
    citycode: string,
    housenumber: string,
    label: string,
    postcode: string,
    score: number,
    street: string,
}
export const searchAddress = async ({ searchValue }: searchAddressParams): Promise<searchAddressResult[]> => {
    try {
        const { data } = await axiosInstance.get<searchAddressResult[]>(`/hub/referential/address?street=${encodeURIComponent(searchValue)}&limit=20`);
        return Promise.resolve(data);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Searches for places based on the given parameters.
 *
 * @param {searchPlacesParams} params - The parameters for the search.
 * @param {string} params.country - The country for the search.
 * @param {string} params.debouncedQuery - The value to search for.
 * @return {Promise<searchPlacesResult[]>} A promise that resolves to an array of search results.
 * @throws {Error} If an error occurs during the search.
 */
type searchPlacesParams = {
    debouncedQuery: string;
    country: string;
}
type searchPlacesResult = {
    zip_city: string;
    zip_code: string;
}
export const searchPlaces = async ({ country, debouncedQuery }: searchPlacesParams): Promise<searchPlacesResult[]> => {
    try {
        const formattedValue = encodeURIComponent(debouncedQuery.trim().toUpperCase())
        let search: string;
        if (formattedValue.match(/^-?\d+$/)) {
            search = `zipcode=${formattedValue}`
        } else {
            search = `name=${formattedValue}`
        }
        const { data } = await axiosInstance.get<searchPlacesResult[]>(`/hub/referential/cities?country=${country}&${search}`);
        return Promise.resolve(data);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Searches for a company based on the given search value.
 *
 * @param {Object} searchValue - The value to search for.
 * @returns {Promise<any>} A promise that resolves with the search results.
 */
type searchCompanyProps = {
    searchValue: string;
}
export const searchCompany = async ({ searchValue }: searchCompanyProps): Promise<any> => {
    const natures = ["stock"]
    try {
        const { data } = await axiosInstance.get<any>(`/hub/instruments/search/${searchValue}?aggregations=none&format=common&nature=${natures.join('|')}`);
        return Promise.resolve(data);
    } catch (err: any) {
        return Promise.reject(err);
    }
}

/**
 * Retrieves the data of a company from the API.
 *
 * @param {getCompanyDataDataProps} params - The parameters to retrieve the data.
 * @param {string} params.sirenNumber - The SIREN number of the company.
 * @returns {Promise<any>} A promise that resolves with the data of the company.
 */
type getCompanyDataDataProps = {
    sirenNumber: string
}
export const getCompanyData = async({ sirenNumber }: getCompanyDataDataProps): Promise<any> => {
    try {
        const { data } = await axiosInstance.get(`/hub/referential/company?siren=${sirenNumber}`);
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error);
    }
}
